import { Box, BoxProps, Snackbar, styled } from "@mui/material";
import { SenecSnackbarProps } from "src/components/feedback/senec-snackbar/SenecSnackbar";
import { SenecBaseTheme } from "src/styles/senecBaseTheme";
import { merge } from "lodash-es";

type ThemeWithProps = {
    theme: SenecBaseTheme;
    props: Pick<SenecSnackbarProps, "severity" | "anchorOrigin">;
};

export const FlexBox = styled(Box)<BoxProps>(() => {
    return {
        display: "flex",
    };
});

export const TitleBox = styled(Box)<BoxProps>(() => {
    return {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "1rem",
    };
});

export const StyledSnackbar = styled(Snackbar)<Pick<SenecSnackbarProps, "severity" | "anchorOrigin">>(({
    theme: muiTheme,
    ...props
}) => {
    const theme = muiTheme as SenecBaseTheme;
    const themeWithProps = { theme, props };
    return merge(basicSnackbar(themeWithProps), fixedSnackbar(themeWithProps), relativeSnackbar(themeWithProps));
});

const basicSnackbar = (themeWithProps: ThemeWithProps) => {
    return merge({
        "& .MuiPaper-root": {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: backgroundColor(themeWithProps),
            borderRadius: "12px",
            padding: "24px",
            border: `1px solid ${themeWithProps.theme.palette.neutralGray30.main}`,
            color: textColor(themeWithProps),
            "& #alertIcon > .MuiSvgIcon-root": {
                color: iconColor(themeWithProps),
            },
            "& .MuiBox-root .MuiIconButton-root": {
                width: "24px",
                height: "24px",
                marginLeft: "12px",
                background: "none !important",
            },
        },
        [themeWithProps.theme.breakpoints.down("tablet")]: {
            width: "100%",
        },
    });
};

const fixedSnackbar = ({ theme, props }: ThemeWithProps) => {
    const verticalCenterProps = {
        top: "50% !important",
        bottom: "auto",
        transform:
            props.anchorOrigin?.horizontal === "center"
                ? "translateX(-50%) translateY(-50%) !important"
                : "translateY(-50%) !important",
    };

    const verticalCenterPropsTablet = {
        transform: "translateY(-50%) !important",
    };

    return (
        props.anchorOrigin && {
            width: "auto",
            position: "fixed",
            ...(props.anchorOrigin.vertical === "center" ? verticalCenterProps : {}),
            [theme.breakpoints.down("tablet")]: {
                width: "auto",
                ...(props.anchorOrigin.vertical === "center" ? verticalCenterPropsTablet : {}),
            },
        }
    );
};

const relativeSnackbar = ({ props }: ThemeWithProps) => {
    return (
        !props.anchorOrigin && {
            width: "100%",
            position: "relative",
            top: "0 !important",
            bottom: "0 !important",
            left: "0 !important",
            right: "0 !important",
        }
    );
};

const backgroundColor = ({ theme, props }: ThemeWithProps) => {
    switch (props.severity) {
        case "info":
            return theme.palette.neutralGray10.main;
        case "success":
            return theme.palette.limeGreen00.main;
        case "warning":
            return theme.palette.sunYellow00.main;
        case "error":
            return theme.palette.tarocco00.main;
    }
};

const iconColor = ({ theme, props }: ThemeWithProps) => {
    switch (props.severity) {
        case "info":
            return theme.palette.deepBlue.main;
        case "error":
            return theme.palette.red.main;
        case "success":
            return theme.palette.green.main;
        case "warning":
            return theme.palette.yellow.main;
    }
};

const textColor = ({ theme, props }: ThemeWithProps) => {
    switch (props.severity) {
        case "info":
            return theme.palette.neutralGray100.main;
        case "error":
            return theme.palette.tarocco90.main;
        case "success":
            return theme.palette.limeGreen90.main;
        case "warning":
            return theme.palette.sunYellow90.main;
    }
};
